import {
  RollbackStageCommand,
  GetPipelineStateCommand,
} from "@aws-sdk/client-codepipeline";
import { getPipelineClient } from "./pipeline";
import type { Stage } from "../types";

const PIPELINE_NAME = "pos-system-deploy";

/**
 * Stage name mapping for the pipeline.
 */
const STAGE_NAMES: Record<Stage, string> = {
  dev: "Deploy_Dev",
  beta: "Deploy_Beta",
  prod: "Deploy_Prod",
};

/**
 * Perform an actual pipeline rollback on a specific stage.
 * 
 * CodePipeline V2 supports RollbackStage which reverts a stage
 * to its previous successful execution — deploying the prior artifacts
 * without re-running the full pipeline.
 */
export async function rollbackPipelineStage(targetStage: Stage): Promise<string> {
  const client = getPipelineClient();
  const stageName = STAGE_NAMES[targetStage];

  if (!stageName) {
    throw new Error(`Invalid stage: ${targetStage}`);
  }

  // Get current pipeline state to find the target stage's execution ID
  const stateResp = await client.send(
    new GetPipelineStateCommand({ name: PIPELINE_NAME })
  );

  const stageState = stateResp.stageStates?.find((s) => s.stageName === stageName);
  if (!stageState?.latestExecution?.pipelineExecutionId) {
    throw new Error(`No execution found for stage ${stageName}`);
  }

  // Execute the rollback — this reverts the stage to its previous successful execution
  const response = await client.send(
    new RollbackStageCommand({
      pipelineName: PIPELINE_NAME,
      stageName,
      targetPipelineExecutionId: stageState.latestExecution.pipelineExecutionId,
    })
  );

  return response.pipelineExecutionId || "";
}

/**
 * Get the previous successful execution ID for a stage.
 * Used when RollbackStage needs a target execution.
 */
export async function getPreviousExecution(targetStage: Stage): Promise<string | null> {
  const client = getPipelineClient();
  const stageName = STAGE_NAMES[targetStage];

  const stateResp = await client.send(
    new GetPipelineStateCommand({ name: PIPELINE_NAME })
  );

  const stageState = stateResp.stageStates?.find((s) => s.stageName === stageName);
  
  // The current execution is what we want to roll back FROM
  // RollbackStage will automatically use the previous successful one
  return stageState?.latestExecution?.pipelineExecutionId || null;
}
